/**
 * Turning to a schedule.
 *
 * A scan is a synthetic aperture, and an aperture is only as good as the
 * trajectory that swept it. The reconstruction assumes the user turned once
 * about a fixed axis at a roughly constant rate, and every quality figure the
 * app reports afterwards - angular coverage, pitch spread, the fitted arm
 * radius - is really a measure of how nearly that happened.
 *
 * Until now the whole of that instruction was the phrase "about twenty
 * seconds" printed on the page, and the grading arrived after the twenty
 * seconds were spent. Two things were wrong with it. The first is that twenty
 * seconds is only right for one of the three scan lengths: eighty chirps take
 * thirty, and a user who turns in twenty then stands still for ten spends a
 * sixth of the scan firing chirps from one bearing, which adds no aperture and
 * drags the autofocus towards a radius that fits a stationary phone. The
 * second is that nobody can pace a half-minute turn by feel.
 *
 * So the schedule is computed rather than described, and the numbers here are
 * what the cue track and the on-screen readout are both driven from. Pure and
 * synchronous, like the rest of `dsp/` - the audio layer decides how to make a
 * sound, this decides when one is due.
 */

export interface TimelineSpec {
  /** How many chirps the scan fires. */
  looks: number;
  /** Length of one chirp. */
  chirpSec: number;
  /** Silence between the end of one chirp and the start of the next. */
  restSec: number;
  /** Silence before the first chirp. */
  leadInSec?: number;
  /** Recording that continues past the last chirp, to catch its tail. */
  tailSec?: number;
  /** The ready beat: time between the button and the first chirp. */
  countdownSec?: number;
}

export interface Timeline {
  looks: number;
  chirpSec: number;
  restSec: number;
  leadInSec: number;
  tailSec: number;
  countdownSec: number;
  /** Chirp to chirp. */
  sliceSec: number;
  /** How long the user has to get all the way round. */
  turnSec: number;
  /** Countdown plus lead-in plus every chirp plus the tail. */
  totalSec: number;
  /** Mean heading rate the schedule asks for, radians per second. */
  turnRateRadPerSec: number;
}

export function scanTimeline(spec: TimelineSpec): Timeline {
  const looks = Math.max(1, Math.round(spec.looks));
  const chirpSec = spec.chirpSec;
  const restSec = spec.restSec;
  const leadInSec = spec.leadInSec ?? 0.35;
  const tailSec = spec.tailSec ?? 0.35;
  const countdownSec = spec.countdownSec ?? 0;
  const sliceSec = chirpSec + restSec;
  // The turn is timed against the chirping, not against the whole run: the
  // lead-in exists so the worklet is certainly awake and the tail to catch the
  // last decay, and neither is time the user should be turning through.
  const turnSec = looks * sliceSec;
  return {
    looks,
    chirpSec,
    restSec,
    leadInSec,
    tailSec,
    countdownSec,
    sliceSec,
    turnSec,
    totalSec: countdownSec + leadInSec + turnSec + tailSec,
    turnRateRadPerSec: (2 * Math.PI) / turnSec,
  };
}

/** When chirp `k` starts, seconds after the first chirp starts. */
export function chirpStartSec(timeline: Timeline, k: number): number {
  return k * timeline.sliceSec;
}

/**
 * The quiet window after chirp `k`, as [start, end) seconds from the first
 * chirp.
 *
 * Every cue the app makes has to live in one of these. See `audio/cues.ts` for
 * why that is not merely tidy.
 */
export function gapSec(timeline: Timeline, k: number): [number, number] {
  const start = chirpStartSec(timeline, k) + timeline.chirpSec;
  return [start, start + timeline.restSec];
}

/** Where the schedule says the user should be pointing at time `t`. */
export function idealHeadingRad(timeline: Timeline, tSec: number): number {
  const clamped = Math.max(0, Math.min(timeline.turnSec, tSec));
  return clamped * timeline.turnRateRadPerSec;
}

export type PaceVerdict = 'on' | 'behind' | 'ahead';

/**
 * How the turn is going, from the heading swept so far.
 *
 * Signed error in radians: positive means the user has turned further than the
 * schedule asked for by now. The tolerance is deliberately loose - a third of
 * a right angle - because the point of the cue is to stop a scan going badly
 * wrong, not to make someone chase a metronome. Coverage is what actually
 * matters and coverage survives a lot of wobble; what it does not survive is
 * finishing the turn at the halfway mark, or still facing the start bearing
 * with a third of the chirps left.
 */
export function paceVerdict(
  sweptRad: number,
  idealRad: number,
  toleranceRad = Math.PI / 6,
): { error: number; verdict: PaceVerdict } {
  const error = sweptRad - idealRad;
  if (error > toleranceRad) return { error, verdict: 'ahead' };
  if (error < -toleranceRad) return { error, verdict: 'behind' };
  return { error, verdict: 'on' };
}

export interface PacingCue {
  /** The chirp this cue follows; the sound goes in the gap after it. */
  lookIndex: number;
  /** Seconds from the first chirp to the start of that gap. */
  atSec: number;
  /** Heading the schedule wants by then, radians. */
  idealHeadingRad: number;
  /** True on the quarter turns, which get a distinguishable cue. */
  quarter: boolean;
}

/**
 * The beat to turn by.
 *
 * Evenly spaced in *time*, not in heading, because a person following a sound
 * is matching a rhythm rather than reading a protractor - and a beat that is
 * even in time is exactly what produces a turn that is even in heading, which
 * is the trajectory the reconstruction assumes.
 *
 * Sixteen beats to the turn by default: a beat every 22.5 degrees, which at
 * standard scan length is one every 1.3 seconds. Fast enough to correct
 * against, slow enough not to become noise.
 */
export function pacingCues(timeline: Timeline, beats = 16): PacingCue[] {
  const cues: PacingCue[] = [];
  if (timeline.looks < 2 || beats < 1) return cues;
  const perBeat = timeline.looks / beats;
  let previous = -1;
  for (let b = 1; b <= beats; b++) {
    // Snap to the gap after a chirp, and never twice after the same one: a
    // short scan can ask for more beats than it has chirps to hang them on.
    const lookIndex = Math.min(timeline.looks - 1, Math.max(0, Math.round(b * perBeat) - 1));
    if (lookIndex <= previous) continue;
    previous = lookIndex;
    cues.push({
      lookIndex,
      atSec: gapSec(timeline, lookIndex)[0],
      idealHeadingRad: (b / beats) * 2 * Math.PI,
      quarter: (b * 4) % beats === 0,
    });
  }
  return cues;
}

/**
 * Whether to ask for more nodding, and when.
 *
 * The floor and the ceiling are the two surfaces a level phone cannot place,
 * and `pitchSpreadDeg < 25` is the warning the reconstruction issues about it
 * afterwards. Afterwards is too late, so the same threshold is checked while
 * there is still scan left to fix it with - but only at a couple of points,
 * because a cue that fires every beat stops being information.
 */
export function tiltCheckpoints(timeline: Timeline, fractions = [0.35, 0.65]): number[] {
  return fractions
    .map((f) => Math.round(f * (timeline.looks - 1)))
    .filter((k, i, all) => k > 0 && k < timeline.looks - 1 && all.indexOf(k) === i);
}

export const TILT_TARGET_DEG = 25;

/** Seconds, rendered the way the instruction line wants to read them. */
export function describeDuration(seconds: number): string {
  const whole = Math.round(seconds);
  if (whole < 60) return `${whole} seconds`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  if (rest === 0) return minutes === 1 ? 'a minute' : `${minutes} minutes`;
  return `${minutes} min ${rest} s`;
}
